import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Smartphone,
  CreditCard,
  BarChart3,
  Bell,
  ListChecks,
  CalendarCheck,
} from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      icon: <Smartphone className='w-5 h-5' />,
      question: 'O FinZiin é realmente gratuito?',
      answer:
        'Sim! O plano FinZiin - Gratuito para Sempre oferece todas as funcionalidades essenciais, sem limites de contas ou cartões. Mantemos nossa sustentabilidade com apenas 1 anúncio por vez, sem interferir na sua experiência.',
    },
    {
      icon: <CreditCard className='w-5 h-5' />,
      question: 'Quantas contas e cartões posso adicionar?',
      answer:
        'Ilimitadas! Cadastre todas as suas contas, cartões e investimentos sem restrições, diferentemente de outros apps gratuitos que limitam o número de contas.',
    },
    {
      icon: <BarChart3 className='w-5 h-5' />,
      question: 'Como funciona a previsão de saldo mensal?',
      answer:
        'O FinZiin calcula automaticamente seu saldo futuro considerando receitas, despesas e limites de gastos, oferecendo uma visão clara de quanto terá disponível.',
    },
    {
      icon: <Smartphone className='w-5 h-5' />,
      question: 'Posso usar o app offline?',
      answer:
        'Sim! Você consegue visualizar todos os dados mesmo sem internet. Ao se conectar, tudo é sincronizado automaticamente.',
    },
    {
      icon: <Bell className='w-5 h-5' />,
      question: 'Consigo importar notificações de bancos?',
      answer:
        'Sim! O FinZiin importa notificações bancárias para manter seu controle financeiro sempre atualizado.',
    },
    {
      icon: <HelpCircle className='w-5 h-5' />,
      question: 'O FinZiin oferece suporte?',
      answer:
        'Sempre! Atendimento rápido pelo app, email e FAQ completo para você tirar qualquer dúvida.',
    },
    {
      icon: <CalendarCheck className='w-5 h-5' />,
      question: 'Recebo alertas de vencimentos e pagamentos?',
      answer:
        'Sim! Configure alertas para receitas, despesas e faturas: no dia, ou 1, 3 ou 7 dias antes, no horário que preferir.',
    },
    {
      icon: <CreditCard className='w-5 h-5' />,
      question: 'Como funciona a reserva de saldo do limite de gastos?',
      answer:
        'Você pode ativar a reserva de saldo restante para não considerar o valor disponível do limite de gastos na sua previsão de saldo. O valor reservado será descontado automaticamente da previsão até a data final do limite, e o que sobrar será devolvido à sua previsão automaticamente. Isso ajuda a controlar melhor seus gastos e evita surpresas no fim do período.',
    },
    {
      icon: <BarChart3 className='w-5 h-5' />,
      question: 'Posso gerar relatórios e consultar histórico?',
      answer:
        'Sim! O FinZiin mantém histórico completo e relatórios detalhados de receitas, despesas e saldo, totalmente gratuito.',
    },
    {
      icon: <Smartphone className='w-5 h-5' />,
      question: 'Posso acessar minha conta em diferentes dispositivos?',
      answer:
        'Sim! Basta entrar na sua conta em qualquer dispositivo e seus dados serão sincronizados automaticamente.',
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section id='faq' className='py-40 bg-white'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <h2 className='text-4xl lg:text-5xl font-bold mb-6'>
            Dúvidas
            <span className='gradient-text'> Frequentes</span>
          </h2>
          <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
            Reunimos aqui as respostas para as perguntas mais comuns sobre o
            FinZiin. Se a sua dúvida não estiver aqui, nossa equipe estará
            pronta para ajudar.
          </p>
        </div>

        {/* FAQ Grid */}
        <div className='max-w-4xl mx-auto'>
          <div className='space-y-4'>
            {faqs.map((faq, index) => (
              <div
                key={index}
                className='bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300'
              >
                <button
                  className='w-full text-left py-6 px-8 focus:outline-none'
                  onClick={() => toggleFAQ(index)}
                >
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center space-x-4'>
                      <div className='bg-blue-100 text-blue-600 p-2 rounded-lg'>
                        {faq.icon}
                      </div>
                      <h3 className='text-lg font-semibold text-gray-800 pr-4'>
                        {faq.question}
                      </h3>
                    </div>
                    <div className='text-blue-500'>
                      {openIndex === index ? (
                        <ChevronUp className='w-6 h-6' />
                      ) : (
                        <ChevronDown className='w-6 h-6' />
                      )}
                    </div>
                  </div>
                </button>

                {openIndex === index && (
                  <div className='px-8 pb-6'>
                    <div className='border-t border-gray-200 pt-4 ml-12'>
                      <p className='text-gray-600 leading-relaxed'>
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Support Section */}
        <div className='mt-16 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8'>
          <div className='text-center max-w-2xl mx-auto'>
            <div className='bg-blue-100 text-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6'>
              <HelpCircle className='w-8 h-8' />
            </div>

            <h3 className='text-2xl font-bold mb-4 text-gray-800'>
              Ainda ficou com alguma dúvida?
            </h3>

            <p className='text-gray-600 mb-6'>
              Fique tranquilo: nosso time está sempre à disposição para ajudar.
              Seja diretamente no app ou por email, você terá uma resposta o
              mais breve possível.
            </p>

            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <a
                href='mailto:finziin.app@gmail.com'
                className='bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300'
              >
                Enviar Email
              </a>

              <a
                href='https://play.google.com/store/apps/details?id=br.android.finziin'
                target='_blank'
                rel='noopener noreferrer'
                className='bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300'
              >
                Baixar App
              </a>
            </div>

            <div className='mt-6 text-sm text-gray-500'>
              <p>
                📧 finziin.app@gmail.com • 📱 Suporte rápido direto pelo app •
                ⏰ Resposta em até 24h
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
